import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { NamedPresetControls } from '@/hooks/useNamedPresets'
import { cn } from '@/lib/utils'

export function PresetControls(props: { label: string; presets: NamedPresetControls; className?: string }) {
  return <PresetControlsContent key={props.presets.scope} {...props} />
}

function PresetControlsContent({ label, presets, className }: { label: string; presets: NamedPresetControls; className?: string }) {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<'create' | 'rename' | 'delete'>('create')
  const [name, setName] = useState('')
  const locked = presets.disabled || presets.busy
  const selected = presets.items.find((item) => item.id === presets.selectedId)
  const submit = async () => {
    const ok = mode === 'delete' ? await presets.onDelete()
      : mode === 'rename' ? await presets.onRename(name) : await presets.onCreate(name)
    if (ok) setOpen(false)
  }

  return (
    <div className={cn('min-w-0 space-y-1.5', className)}>
      <div className="flex flex-wrap items-center gap-1.5">
        <select
          aria-label={`${label} preset`}
          className="h-7 min-w-0 max-w-full flex-1 rounded-md border border-input bg-background px-2 text-[11px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
          value={presets.selectedId}
          disabled={locked || presets.items.length === 0}
          onChange={(event) => { if (event.target.value) void presets.onSelect(event.target.value) }}
        >
          <option value="" disabled>{presets.items.length ? 'Choose a preset' : 'No saved presets'}</option>
          {presets.items.map((item) => <option key={item.id} value={item.id}>
            {item.name}{item.id === presets.selectedId && presets.modified ? ' (modified)' : ''}
          </option>)}
        </select>
        {selected && <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-[11px]"
          aria-label={`Update ${label} preset`} disabled={locked || !presets.modified} onClick={() => { void presets.onUpdate() }}>Update</Button>}
        {selected && presets.modified && <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-[11px]"
          aria-label={`Reapply ${label} preset`} disabled={locked} onClick={() => { void presets.onSelect(selected.id) }}>Reapply</Button>}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-[11px]"
              aria-label={`Manage ${label} presets`} disabled={locked}
              onClick={() => { setMode('create'); setName('') }}>Save / manage</Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 space-y-3">
            <p className="text-sm font-medium">{label} presets</p>
            <div className="flex gap-1">
              {(['create', 'rename', 'delete'] as const).map((action) => <Button key={action} type="button" size="sm"
                variant={mode === action ? 'secondary' : 'ghost'} className="h-7 px-2 text-xs"
                disabled={locked || (action !== 'create' && !selected)}
                onClick={() => { setMode(action); setName(action === 'rename' ? selected?.name ?? '' : '') }}>
                {action === 'create' ? 'Save new' : action === 'rename' ? 'Rename' : 'Delete'}
              </Button>)}
            </div>
            {mode === 'delete' ? <p className="text-xs">Delete “{selected?.name}”? Your current settings will stay as they are.</p>
              : <label className="block space-y-1 text-xs">Preset name
                <Input autoFocus value={name} maxLength={80} disabled={locked} aria-label={`${label} preset name`}
                  placeholder="e.g. Morning scan" onChange={(event) => setName(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void submit() } }} />
              </label>}
            <Button type="button" size="sm" variant={mode === 'delete' ? 'destructive' : 'default'}
              disabled={locked || (mode !== 'delete' && !name.trim())} onClick={() => { void submit() }}>
              {presets.busy ? 'Saving…' : mode === 'create' ? 'Save preset' : mode === 'rename' ? 'Rename preset' : 'Delete preset'}
            </Button>
            {presets.error && <p role="alert" className="text-xs text-destructive">{presets.error}</p>}
          </PopoverContent>
        </Popover>
      </div>
      {presets.error && !open && <p role="alert" className="text-xs text-destructive">{presets.error}</p>}
      <p role="status" className="text-[10px] text-muted-foreground">
        {presets.busy ? 'Applying changes…' : presets.modified ? 'Modified — update this preset or save a new one.'
          : presets.status || 'Save named presets to your account and switch instantly.'}
      </p>
    </div>
  )
}
